import React from 'react';
import MobileModal from '../MobileModal';
import Button from './Button';

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
  /** Red error text shown under the message (e.g. after declining a required confirmation). */
  errorMessage?: string;
  /**
   * When set, the secondary button calls this instead of onClose so the dialog can stay open
   * (e.g. Yes/No flows that show an inline error on No). Modal X / backdrop still use onClose.
   */
  onDecline?: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText = 'Cancel',
  variant = 'danger',
  isLoading = false,
  errorMessage,
  onDecline,
}) => {
  const handleConfirm = () => {
    onConfirm();
  };

  const handleSecondary = () => {
    if (onDecline) {
      onDecline();
      return;
    }
    onClose();
  };

  const variantConfig = {
    danger: {
      confirmVariant: 'destructive' as const,
      confirmText: confirmText || 'Delete',
      icon: (
        <svg className="h-6 w-6 text-error-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
    },
    warning: {
      confirmVariant: 'primary' as const,
      confirmText: confirmText || 'Confirm',
      icon: (
        <svg className="h-6 w-6 text-[#FDAB3D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
    },
    info: {
      confirmVariant: 'primary' as const,
      confirmText: confirmText || 'Confirm',
      icon: (
        <svg className="h-6 w-6 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  };

  const config = variantConfig[variant];

  return (
    <MobileModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <div className="flex gap-3 w-full sm:flex-row-reverse">
          <Button
            variant={config.confirmVariant}
            onClick={handleConfirm}
            disabled={isLoading}
            loading={isLoading}
            className="w-full sm:w-auto"
          >
            {config.confirmText}
          </Button>
          <Button
            variant="secondary"
            onClick={handleSecondary}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            {cancelText}
          </Button>
        </div>
      }
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          {config.icon}
        </div>
        <div className="flex-1">
          <p className="text-sm text-monday-text">{message}</p>
          {errorMessage ? (
            <p className="mt-3 text-sm font-medium text-error-600" role="alert">
              {errorMessage}
            </p>
          ) : null}
        </div>
      </div>
    </MobileModal>
  );
};

export default ConfirmDialog;
